import { MarkdownPreview, Spinner } from '@consigli/facade';
import { type FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { DocumentPaneRenderProps } from '@aecom-ai-engineering/react-components';

interface MarkdownRendererPaneProps {
  active: boolean;
  src: DocumentPaneRenderProps['src'];
}

const MarkdownRendererPane: FC<MarkdownRendererPaneProps> = ({ active, src }) => {
  const { t } = useTranslation();
  const [content, setContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fileUrl = typeof src === 'string' ? src : src instanceof URL ? src.href : undefined;

  useEffect(() => {
    if (!active || !fileUrl) return;

    const controller = new AbortController();
    const fetchMarkdown = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const response = await fetch(fileUrl, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Failed to fetch Markdown: ${response.status} ${response.statusText}`);
        }
        const text = await response.text();
        setContent(text);
      } catch (err) {
        if (controller.signal.aborted) return;
        setError(
          err instanceof Error
            ? err.message
            : t('document-viewer.markdown-load-failed', 'Failed to load Markdown'),
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchMarkdown();
    return () => controller.abort();
  }, [active, fileUrl, t]);

  if (!active) return null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full w-full">
        <div className="text-red-500">
          {t('document-viewer.markdown-error', 'Error loading Markdown')}: {error}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full w-full overflow-auto bg-white p-8">
      <MarkdownPreview content={content} />
    </div>
  );
};

export function createMarkdownRenderer() {
  return function MarkdownRenderer(props: DocumentPaneRenderProps) {
    return <MarkdownRendererPane active={props.active} src={props.src} />;
  };
}
